<template>
  <div class="space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">{{ $t("categories") }}</h1>

    <!-- Actions -->
    <div class="flex gap-3">
      <BaseButton type="primary" @click="fetchCategories">
        {{ $t("refresh") }}
      </BaseButton>

      <BaseButton type="success" @click="openCreateModal">
        + {{ $t("add") }}
      </BaseButton>
    </div>

    <SearchBar v-model="search" @search="fetchCategories" />

    <!-- Table -->
    <BaseDataTable :columns="columns" :data="categories">
      <template #actions="{ row }">
        <TableActions
          :row="row"
          show-edit
          show-delete
          @edit="editCategory(row)"
          @delete="deleteCategory(row.id)"
        />
      </template>
    </BaseDataTable>
    <BasePagination
      v-if="pagination"
      :pagination="pagination"
      @change-page="fetchCategories"
    />

    <!-- Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center"
    >
      <div class="bg-white p-6 rounded-lg w-[420px] space-y-4">
        <h2 class="text-xl font-bold">
          {{ isEdit ? $t("edit_item") : $t("create_item") }}
        </h2>

        <!-- Name -->
        <input
          v-model="form.name_en"
          type="text"
          :placeholder="$t('name_en')"
          class="w-full border p-2 rounded"
        />
        <input
          v-model="form.name_ar"
          type="text"
          :placeholder="$t('name_ar')"
          class="w-full border p-2 rounded"
        />

        <!-- Brand -->
        <select v-model="form.brand_id" class="w-full border p-2 rounded">
          <option value="">{{ $t("select_brand") }}</option>
          <option v-for="b in brands" :key="b.id" :value="b.id">
            {{ b.name_en }}
          </option>
        </select>

        <div class="flex justify-end gap-2">
          <BaseButton type="secondary" @click="closeModal">{{
            $t("cancel")
          }}</BaseButton>
          <BaseButton type="success" @click="submitCategory">
            {{ isEdit ? $t("update") : $t("create") }}
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/components/Button.vue";
import BaseDataTable from "@/components/BaseDataTable.vue";
import TableActions from "@/components/TableActions.vue";
import categoryService from "@/services/category.service";
import brandService from "@/services/brand.service";
import BasePagination from "@/components/BasePagination.vue";
import SearchBar from "@/components/SearchBar.vue";

export default {
  name: "CategoryList",
  components: {
    BaseButton,
    BaseDataTable,
    TableActions,
    BasePagination,
    SearchBar,
  },

  data() {
    return {
      categories: [],
      pagination: "",
      search: "",
      brands: [],

      showModal: false,
      isEdit: false,
      currentId: null,

      form: {
        name_en: "",
        name_ar: "",
        brand_id: "",
      },
    };
  },

  computed: {
    columns() {
      return [
        { label: this.$t("id"), field: "id", sortable: true },
        { label: this.$t("name_en"), field: "name_en", sortable: true },
        { label: this.$t("name_ar"), field: "name_ar", sortable: true },
        { label: this.$t("brand"), field: "brand.name_en", sortable: true },
      ];
    },
  },

  methods: {
    /* ============ Fetch ============ */
    async fetchCategories(page = 1) {
      const res = await categoryService.getCategories({
        per_page: 8,
        search: this.search,
        page: page,
      });

      this.categories = res.data.data;
      this.pagination = res.data.pagination;
    },

    async fetchBrands() {
      const res = await brandService.getBrands();
      this.brands = res.data.data;
    },

    /* ============ Create ============ */
    openCreateModal() {
      this.isEdit = false;
      this.currentId = null;
      this.form = {
        name_en: "",
        name_ar: "",
        brand_id: "",
      };
      this.showModal = true;
    },

    async createCategory() {
      await categoryService.createCategory({
        name_en: this.form.name_en,
        name_ar: this.form.name_ar,
        brand_id: this.form.brand_id,
      });
      await this.fetchCategories();
      this.closeModal();
    },
    /* ============ Edit ============ */
    editCategory(cat) {
      this.isEdit = true;
      this.currentId = cat.id;
      this.form = {
        name_en: cat.name_en,
        name_ar: cat.name_ar,
        brand_id: cat.brand_id,
      };
      this.showModal = true;
    },

    async updateCategory() {
      await categoryService.updateCategory(this.currentId, {
        name_en: this.form.name_en,
        name_ar: this.form.name_ar,
        brand_id: this.form.brand_id,
      });
      await this.fetchCategories();
      this.closeModal();
    },
    /* ============ Delete ============ */
    async deleteCategory(id) {
      if (!confirm(this.$t("delete_confirm"))) return;

      try {
        await categoryService.deleteCategory(id);

        // حذف من الواجهة مباشرة
        this.categories = this.categories.filter((cat) => cat.id !== id);
      } catch (e) {
        console.error("Delete error", e);
      }
    },

    /* ============ Submit ============ */
    async submitCategory() {
      if (this.isEdit) {
        await this.updateCategory();
      } else {
        await this.createCategory();
      }
    },

    closeModal() {
      this.showModal = false;
    },
  },

  async mounted() {
    await this.fetchBrands();
    await this.fetchCategories(1);
  },
};
</script>
